<template>
  <div>
    <EdroneTooltip
      :offset="[0, 16]"
      close-trigger-class-name="close-status"
      position="bottom"
      trigger-event="mouseenter"
      type="custom"
    >
      <PackageIndicatorStatus
        :disabled="actor.snapshot.value.hasTag('success') && actor.snapshot.value.context.data?.flatPricing"
        :loading="actor.snapshot.value.hasTag('loading')"
        :progress="actor.snapshot.value.context.data?.engagementPercentage || 0"
        icon="fa-light fa-users"
        size="tw:size-10"
      />

      <template #custom>
        <div
          v-if="actor.snapshot.value.hasTag('success') && actor.snapshot.value.context.data?.flatPricing"
          class="tw:flex tw:flex-col tw:gap-2 tw:p-2 tw:min-w-[16.25rem]"
        >
          <div class="tw:p-3 tw:text-center">
            <p class="tw:body-2">{{ trans.engagementRateStatus.flatPricingMessage }}</p>
          </div>
        </div>
        <div
          v-else-if="actor.snapshot.value.hasTag('success') && actor.snapshot.value.context.data"
          class="tw:flex tw:flex-col tw:gap-2 tw:p-2 tw:min-w-[16.25rem]"
        >
          <div class="tw:flex tw:gap-6">
            <div class="tw:p-3 tw:border-l tw:border-default-200 tw:flex tw:flex-col">
              <p class="tw:body-2 tw:mb-1">{{ trans.engagementRateStatus.contactBaseUtilization }}</p>
              <p class="tw:heading-1 tw:mb-2">
                {{ (actor.snapshot.value.context.data?.engagementPercentage || 0).toFixed(2) }}%
              </p>
              <p v-if="actor.snapshot.value.context.data" class="tw:body-2 tw:text-default-700">
                <b>{{ actor.snapshot.value.context.data.reached }}</b>
                {{
                  trans.engagementRateStatus.paidContactsReached.replace(
                    "{billed}",
                    `${actor.snapshot.value.context.data.billed}`
                  )
                }}
              </p>
            </div>
          </div>
          <div
            v-if="actor.snapshot.value.context.data"
            class="tw:flex tw:flex-col tw:gap-3 tw:pt-4 tw:border-t tw:border-default-200"
          >
            <p class="tw:body-2 tw:font-semibold">{{ trans.engagementRateStatus.channelUtilization }}</p>
            <div class="tw:flex tw:flex-col tw:gap-2">
              <div class="tw:flex tw:items-center tw:justify-between tw:gap-3">
                <div class="tw:flex tw:items-center tw:gap-2 tw:min-w-0 tw:flex-1">
                  <i class="fa-light fa-envelope tw:text-blue-500 tw:w-4 tw:text-center"></i>
                  <span class="tw:body-2 tw:truncate">{{ trans.engagementRateStatus.channels.email }}</span>
                </div>
                <div class="tw:flex tw:items-center tw:gap-2 tw:min-w-[120px]">
                  <EdroneProgressBar
                    :badge="`${(actor.snapshot.value.context.data?.engagementPercentage || 0).toFixed(2)}%`"
                    :value="actor.snapshot.value.context.data?.engagementPercentage || 0"
                    class="tw:flex-1"
                    type="info"
                  />
                </div>
              </div>
              <div class="tw:flex tw:items-center tw:justify-between tw:gap-3">
                <div class="tw:flex tw:items-center tw:gap-2 tw:min-w-0 tw:flex-1">
                  <i class="fa-light fa-comment-sms tw:text-yellow-500 tw:w-4 tw:text-center"></i>
                  <span class="tw:body-2 tw:truncate">{{ trans.engagementRateStatus.channels.sms }}</span>
                </div>
                <div class="tw:flex tw:items-center tw:gap-2 tw:min-w-[120px]">
                  <EdroneProgressBar
                    :badge="`${(actor.snapshot.value.context.data?.smsPercentage || 0).toFixed(2)}%`"
                    :value="actor.snapshot.value.context.data?.smsPercentage || 0"
                    class="tw:flex-1"
                    type="warning"
                  />
                </div>
              </div>
              <div class="tw:flex tw:items-center tw:justify-between tw:gap-3">
                <div class="tw:flex tw:items-center tw:gap-2 tw:min-w-0 tw:flex-1">
                  <i class="fab fa-whatsapp tw:text-green-600 tw:w-4 tw:text-center"></i>
                  <span class="tw:body-2 tw:truncate">{{ trans.engagementRateStatus.channels.whatsapp }}</span>
                </div>
                <div class="tw:flex tw:items-center tw:gap-2 tw:min-w-[120px]">
                  <EdroneProgressBar
                    :badge="`${(actor.snapshot.value.context.data?.whatsappPercentage || 0).toFixed(2)}%`"
                    :value="actor.snapshot.value.context.data?.whatsappPercentage || 0"
                    class="tw:flex-1"
                    type="success"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          v-else-if="actor.snapshot.value.hasTag('loading')"
          class="tw:flex tw:flex-col tw:gap-2 tw:p-2 tw:min-w-[16.25rem]"
        >
          <EdroneResultState type="loading" />
        </div>
        <div
          v-else-if="actor.snapshot.value.hasTag('error')"
          class="tw:flex tw:flex-col tw:gap-2 tw:p-2 tw:min-w-[16.25rem]"
        >
          <EdroneResultState type="error">
            <p class="tw:m-0">Error</p>
          </EdroneResultState>
        </div>
      </template>
    </EdroneTooltip>
  </div>
</template>

<script lang="ts" setup>
import EdroneTooltip from "edrone-components/EdroneTooltip.vue";
import PackageIndicatorStatus from "vue-app/components/PackageIndicator/PackageIndicatorStatus.vue";
import { useMachine } from "@xstate-5/vue";
import { inspector } from "vue-app/utils/xstate.ts";
import { provide } from "vue";
import { machine, symbol } from "vue-app/components/EngagementRateStatus/EngagementRateStatus.machine.ts";
import EdroneResultState from "edrone-components/EdroneResultState.vue";
import EdroneProgressBar from "vue-app/edroneComponents/EdroneProgressBar.vue";

const actor = useMachine(machine, { inspect: inspector().inspect });
provide(symbol, actor);

const trans = window._trans._billing;
</script>
